import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { showSuccess, showError } from '../../utils/toast';
import '../../App.css';

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const VehicleStep = ({ onNext, onBack, onSkip, onboardingData, setOnboardingData, userId }) => {
  const [formData, setFormData] = useState({
    nickname: '',
    vin: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    licensePlate: '',
    licensePlateState: '',
    lastInspectionDate: '',
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'year' ? parseInt(value) || new Date().getFullYear() : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const vehicleData = {
        ...formData,
        lastInspectionDate: formData.lastInspectionDate || undefined,
      };

      await axios.post(`${API_URL}/api/vehicles`, vehicleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setOnboardingData({ ...onboardingData, vehicleAdded: true });
      showSuccess('Vehicle added successfully!');

      const updatedData = { ...onboardingData };
      updatedData.progress = {
        step: 3,
        completed: ['welcome', onboardingData.medicalCardUploaded ? 'medical' : null, 'vehicle'].filter(Boolean),
      };
      setOnboardingData(updatedData);
      onNext();
    } catch (error) {
      console.error('Error adding vehicle:', error);
      const msg = error.response?.data?.message;
      const message = Array.isArray(msg) ? msg.join(' ') : msg || 'Failed to add vehicle. Please try again.';
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    const updatedData = { ...onboardingData };
    updatedData.progress = {
      step: 3,
      completed: ['welcome', onboardingData.medicalCardUploaded ? 'medical' : null].filter(Boolean),
    };
    setOnboardingData(updatedData);
    onSkip();
  };

  return (
    <div className="onboarding-content">
      <h2 className="step-title">Add your first vehicle</h2>
      <p className="step-description">
        Track inspections and maintenance to keep your compliance record current.
      </p>

      <div className="onboarding-form">
        <div className="form-group">
          <label>Vehicle Name/Nickname</label>
          <input
            type="text"
            name="nickname"
            value={formData.nickname}
            onChange={handleChange}
            placeholder="Big Blue"
          />
        </div>

        <div className="form-group">
          <label>VIN</label>
          <input
            type="text"
            name="vin"
            value={formData.vin}
            onChange={handleChange}
            placeholder="1HGBH41JXMN109186"
            className={errors.vin ? 'error' : ''}
          />
          {errors.vin && <span className="error-text">{errors.vin}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Year</label>
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleChange}
              min="1900"
              max={new Date().getFullYear() + 1}
            />
          </div>

          <div className="form-group">
            <label>Make</label>
            <input
              type="text"
              name="make"
              value={formData.make}
              onChange={handleChange}
              placeholder="Freightliner"
            />
          </div>

          <div className="form-group">
            <label>Model</label>
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="Cascadia"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>License Plate</label>
            <input
              type="text"
              name="licensePlate"
              value={formData.licensePlate}
              onChange={handleChange}
              placeholder="ABC-1234"
            />
          </div>

          <div className="form-group">
            <label>License Plate State</label>
            <select
              name="licensePlateState"
              value={formData.licensePlateState}
              onChange={handleChange}
            >
              <option value="">Select State</option>
              {usStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Last DOT Inspection Date (Optional)</label>
          <input
            type="date"
            name="lastInspectionDate"
            value={formData.lastInspectionDate}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="onboarding-buttons">
        <button className="btn btn-secondary skip-button" onClick={handleSkip}>
          Skip for now
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? 'Adding...' : 'Continue →'}
        </button>
      </div>
    </div>
  );
};

export default VehicleStep;

